/**
 * Utilitaires de gestion des fuseaux horaires utilisant date-fns-tz
 */
import { format, formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { fr } from 'date-fns/locale';
import type { Locale } from 'date-fns';

/**
 * Stocke le fuseau horaire actuel du professionnel
 * Par défaut, on utilise Europe/Paris
 */
let currentTimeZone = 'Europe/Paris';

/**
 * Liste des fuseaux horaires couramment utilisés en Europe
 */
export const COMMON_EUROPEAN_TIMEZONES = [
  'Europe/Paris',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Brussels',
  'Europe/Amsterdam',
  'Europe/Zurich',
  'Europe/Lisbon',
  'Europe/Dublin'
];

export function formatTime(): string {
  const currentTime: Date = new Date();
  const zonedTime = toZonedTime(currentTime, currentTimeZone);
  return format(zonedTime, 'HH:mm', { locale: fr });
}

/**
 * Définit le fuseau horaire à utiliser dans l'application
 * @param timeZone - Fuseau horaire au format IANA (ex: 'Europe/Paris', 'America/New_York')
 */
export function setTimeZone(timeZone: string): void {
  currentTimeZone = timeZone;
}

/**
 * Récupère le fuseau horaire actuel
 * @returns Le fuseau horaire actuel
 */
export function getTimeZone(): string {
  return currentTimeZone;
}

/**
 * Formate le décalage horaire d'un fuseau horaire
 * @param timeZone - Le fuseau horaire à formater
 * @param date - Date de référence pour le calcul du décalage (par défaut: date actuelle)
 * @returns Chaîne formatée (ex: "(UTC+1)")
 */
export function formatTimeZoneOffset(timeZone: string, date = new Date()): string {
  try {
    // Obtenir la date au format ISO dans le fuseau horaire spécifié
    const zonedDate = toZonedTime(date, timeZone);
    
    // Extraire le décalage horaire de la chaîne formatée
    const formattedDate = format(zonedDate, 'xxx', { timeZone });
    
    // Simplifier le format pour n'afficher que les heures
    const match = formattedDate.match(/([+-])(\d{2})/);
    if (match) {
      const [, sign, hours] = match;
      return `(UTC${sign}${parseInt(hours)})`;
    }
    
    return '';
  } catch (e) {
    console.warn(`Could not get offset for timezone: ${timeZone}`, e);
    return '';
  }
}

/**
 * Génère la liste des options de fuseau horaire pour le composant de sélection
 * @returns Liste d'options avec valeur et libellé
 */
export function getTimeZoneOptions(): { value: string; label: string }[] {
  return COMMON_EUROPEAN_TIMEZONES.map(timeZone => ({
    value: timeZone,
    label: `${timeZone} ${formatTimeZoneOffset(timeZone)}`
  }));
}

/**
 * Convertit une date UTC en date dans le fuseau horaire du professionnel
 * @param utcDate - Date UTC à convertir (Date ou chaîne ISO)
 * @returns Date dans le fuseau horaire du professionnel
 */
export function utcToLocalTime(utcDate: Date | string): Date {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  return toZonedTime(date, currentTimeZone);
}

/**
 * Convertit une date locale en date UTC
 * @param localDate - Date locale à convertir (Date ou chaîne ISO)
 * @returns Date UTC
 */
export function localToUtcTime(localDate: Date | string): Date {
  const date = typeof localDate === 'string' ? new Date(localDate) : localDate;
  
  // Obtenir l'offset en minutes entre l'heure locale et UTC
  const offsetInMinutes = date.getTimezoneOffset();
  
  // Créer une nouvelle date en ajoutant l'offset (attention: getTimezoneOffset retourne la différence en minutes dans le sens opposé)
  const utcDate = new Date(date.getTime() + offsetInMinutes * 60000);
  
  return utcDate;
}
/**
 * Formate une date dans le fuseau horaire du professionnel
 * @param date - Date à formater (Date ou chaîne ISO)
 * @param formatStr - Format de date (par défaut: dd/MM/yyyy)
 * @param options - Options supplémentaires
 * @returns Chaîne formatée
 */
export function formatDateInTimeZone(
  date: Date | string,
  formatStr: string = 'dd/MM/yyyy',
  options: { locale?: Locale } = { locale: fr }
): string {
  return formatInTimeZone(date, currentTimeZone, formatStr, options);
}

/**
 * Formate une heure dans le fuseau horaire du professionnel
 * @param date - Date contenant l'heure à formater (Date ou chaîne ISO)
 * @param formatStr - Format d'heure (par défaut: HH:mm)
 * @param options - Options supplémentaires
 * @returns Chaîne formatée
 */
export function formatTimeInTimeZone(
  date: Date | string,
  formatStr: string = 'HH:mm',
  options: { locale?: Locale } = { locale: fr }
): string {
  return formatInTimeZone(date, currentTimeZone, formatStr, options);
}
